import { apiClient as api, ApiResponse } from './api';

// Request cache and throttling for cart operations
interface CacheEntry {
  data: any;
  timestamp: number;
}

class CartRequestThrottler {
  private cache: Map<string, CacheEntry> = new Map();
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private readonly CACHE_DURATION = 15000; // 15 seconds for cart data
  private readonly THROTTLE_DELAY = 500; // 500ms between same requests
  private lastRequestTime: Map<string, number> = new Map();

  async throttledRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    // Check cache first
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    // Check if request is already pending
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    // Check throttling
    const lastRequest = this.lastRequestTime.get(key) || 0;
    const timeSinceLastRequest = Date.now() - lastRequest;
    if (timeSinceLastRequest < this.THROTTLE_DELAY) {
      await new Promise(resolve => setTimeout(resolve, this.THROTTLE_DELAY - timeSinceLastRequest));
    }

    // Make the request
    const requestPromise = requestFn().then(result => {
      // Cache the result for GET operations only
      if (key.includes('getCart') || key.includes('getCartSummary')) {
        this.cache.set(key, { data: result, timestamp: Date.now() });
      }
      this.lastRequestTime.set(key, Date.now());
      this.pendingRequests.delete(key);
      return result;
    }).catch(error => {
      this.pendingRequests.delete(key);
      throw error;
    });

    this.pendingRequests.set(key, requestPromise);
    return requestPromise;
  }

  clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Clear cache for specific operations that modify cart
  clearCacheForUser(): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => 
      key.includes('getCart') || key.includes('getCartSummary')
    );
    keysToDelete.forEach(key => this.cache.delete(key));
  }
}

const cartRequestThrottler = new CartRequestThrottler();

export interface CartItem {
  CreatedDate: string;
  WUID: string;
  Plvl: string;
  SupersedeNoUOM: string;
  SupersedeNo: string;
  UOM: string;
  Price: number;
  Qty: number;
  TotalPrice: number;
  ConvRate: number;
  DeliveryCalUnit: number;
  PriceDesc1: string;
  PriceDesc2: string;
  Description: string;
  pathSupersede: string;
  MainPictureFull: string;
  MainPictureName: string;
  Original: string;
}

export interface CartCalculation {
  totalStr: string;
  totalD: number;
  deliveryAmtStr: string;
  deliveryAmtD: number;
  gstAmtStr: string;
  gstAmtD: number;
  gTotalStr: string;
  gTotalD: number;
  gstStatusStr: string;
  gstPercent: number;
  currencyCodeStr: string;
  totalQtyD: number;
}

export interface CartSummary {
  itemCount: number;
  totalQuantity: number;
  subtotal: number;
  calculation: CartCalculation;
}

export interface CartValidation {
  hasChanges: boolean;
  invalidItems: string[];
  priceChanges: CartItem[];
}

export interface CartResponse {
  success: boolean;
  data: {
    items: CartItem[];
    summary: CartSummary;
    hasChanges?: boolean;
    invalidItems?: string[];
    priceChanges?: CartItem[];
    validation?: CartValidation;
    message?: string;
  };
}

export class CartService {
  static async getCart(): Promise<CartResponse> {
    const cacheKey = 'getCart';
    return cartRequestThrottler.throttledRequest(cacheKey, async () => {
      try {
        const response = await api.get<{
          items: CartItem[];
          summary: CartSummary;
          hasChanges?: boolean;
          invalidItems?: string[];
          priceChanges?: CartItem[];
          validation?: CartValidation;
          message?: string;
        }>('/cart');

        return {
          success: response.success,
          data: response.data!
        };
      } catch (error) {
        console.error('Error fetching cart:', error);
        throw error;
      }
    });
  }

  static async addToCart(supersedeNo: string, uom: string, quantity: number = 1): Promise<CartResponse> {
    try {
      const response = await api.post<{
        items: CartItem[];
        summary: CartSummary;
        message?: string;
      }>('/cart/add', {
        supersedeNo,
        uom,
        quantity
      });

      // Clear cache since cart was modified
      cartRequestThrottler.clearCacheForUser();

      return {
        success: response.success,
        data: response.data!
      };
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  static async updateCartItem(supersedeNoUOM: string, quantity: number): Promise<CartResponse> {
    try {
      const response = await api.put<{
        items: CartItem[];
        summary: CartSummary;
        message?: string;
      }>('/cart/update', {
        supersedeNoUOM,
        quantity
      });

      // Clear cache since cart was modified
      cartRequestThrottler.clearCacheForUser();

      return {
        success: response.success,
        data: response.data!
      };
    } catch (error) {
      console.error('Error updating cart item:', error);
      throw error;
    }
  }

  static async removeFromCart(supersedeNoUOM: string): Promise<CartResponse> {
    try {
      const encodedId = encodeURIComponent(supersedeNoUOM);
      const response = await api.delete<{
        items: CartItem[];
        summary: CartSummary;
        message?: string;
      }>(`/cart/remove/${encodedId}`);

      // Clear cache since cart was modified
      cartRequestThrottler.clearCacheForUser();

      return {
        success: response.success,
        data: response.data!
      };
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  }

  static async clearCart(): Promise<CartResponse> {
    try {
      const response = await api.delete<{
        items: CartItem[];
        summary: CartSummary;
        message?: string;
      }>('/cart/clear');

      // Clear cache since cart was modified
      cartRequestThrottler.clearCacheForUser();

      return {
        success: response.success,
        data: response.data!
      };
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  }

  static async validateCart(): Promise<CartResponse> {
    try {
      const response = await api.post<{
        items: CartItem[];
        summary: CartSummary;
        validation?: CartValidation;
      }>('/cart/validate');

      return {
        success: response.success,
        data: response.data!
      };
    } catch (error) {
      console.error('Error validating cart:', error);
      throw error;
    }
  }

  static async getCartSummary(): Promise<{ success: boolean; data: CartSummary }> {
    const cacheKey = 'getCartSummary';
    return cartRequestThrottler.throttledRequest(cacheKey, async () => {
      try {
        const response = await api.get<CartSummary>('/cart/summary');

        return {
          success: response.success,
          data: response.data!
        };
      } catch (error) {
        console.error('Error fetching cart summary:', error);
        throw error;
      }
    });
  }
}